/**
 * Driver-side recovery for browser-observed attachment navigations.
 *
 * @module playwright-client/download-recovery
 */

import type { BrowserContext, CDPSession, Page } from 'playwright';
import { TIMEOUTS } from '../constants.js';
import type { BrowserLogger } from '../logger.js';
import { withOperationTimeout } from '../with-operation-timeout.js';
import type { DownloadCaptureStore } from './downloads.js';

export const DOWNLOAD_ASSERTION_MIN_TIMEOUT_MS = 120_000;
export const MAX_RECOVERED_DOWNLOAD_BYTES = 50 * 1024 * 1024;

export type RecoveredDownloadContentLength =
  | { valid: true; contentLength: number }
  | {
      valid: false;
      reason: 'invalid-content-length' | 'empty-content' | 'content-too-large';
    };

export function validateRecoveredDownloadContentLength(
  rawContentLength?: string
): RecoveredDownloadContentLength {
  const contentLength = Number(rawContentLength);
  if (!Number.isFinite(contentLength)) {
    return { valid: false, reason: 'invalid-content-length' };
  }
  if (contentLength < 1) return { valid: false, reason: 'empty-content' };
  if (contentLength > MAX_RECOVERED_DOWNLOAD_BYTES) {
    return { valid: false, reason: 'content-too-large' };
  }
  return { valid: true, contentLength };
}

export function resolveDownloadAssertionTimeout(requestedTimeoutMs?: number): number {
  if (
    requestedTimeoutMs === undefined ||
    !Number.isFinite(requestedTimeoutMs) ||
    requestedTimeoutMs <= DOWNLOAD_ASSERTION_MIN_TIMEOUT_MS
  ) {
    return DOWNLOAD_ASSERTION_MIN_TIMEOUT_MS;
  }
  return requestedTimeoutMs;
}

export function filenameMatches(filename: string, pattern: string): boolean {
  if (!pattern || pattern === '*') return true;
  const escaped = pattern
    .replace(/[.+^${}()|[\]\\]/g, '\\$&')
    .replace(/\*/g, '.*')
    .replace(/\?/g, '.');
  return new RegExp(`^${escaped}$`, 'i').test(filename);
}

export function asDownloadExpectation(expected: unknown): {
  contains?: string;
  pattern?: string;
} {
  if (!expected || typeof expected !== 'object' || Array.isArray(expected)) return {};
  const candidate = expected as Record<string, unknown>;
  return {
    ...(typeof candidate.contains === 'string' ? { contains: candidate.contains } : {}),
    ...(typeof candidate.pattern === 'string' ? { pattern: candidate.pattern } : {}),
  };
}

export function looksLikeDownloadUrl(rawUrl: string): boolean {
  try {
    const url = new URL(rawUrl);
    return (
      /\.(csv|tsv|pdf|json|xml|zip|txt|xlsx?|docx?|png|jpe?g)$/i.test(url.pathname) ||
      /(?:download|export|attachment|file)/i.test(`${url.pathname}?${url.searchParams.toString()}`)
    );
  } catch {
    return false;
  }
}

export type DownloadObservationCandidate = {
  timestamp: string;
  url: string;
};

export function hasObservedDownloadForUrl(
  downloads: readonly DownloadObservationCandidate[],
  url: string,
  startedAtMs: number
): boolean {
  return downloads.some(
    (download) => download.url === url && Date.parse(download.timestamp) >= startedAtMs
  );
}

export type DownloadResponseClassification =
  | { recoverable: true; reason: 'attachment' | 'download-like-non-html' }
  | { recoverable: false; reason: 'html-without-attachment' | 'not-download-like' };

export function classifyDownloadResponse(input: {
  url: string;
  contentType: string;
  contentDisposition: string;
}): DownloadResponseClassification {
  if (/^\s*attachment(?:\s*;|$)/i.test(input.contentDisposition)) {
    return { recoverable: true, reason: 'attachment' };
  }
  const normalizedContentType = input.contentType.toLowerCase();
  if (
    normalizedContentType.includes('text/html') ||
    normalizedContentType.includes('application/xhtml+xml')
  ) {
    return { recoverable: false, reason: 'html-without-attachment' };
  }
  if (looksLikeDownloadUrl(input.url)) {
    return { recoverable: true, reason: 'download-like-non-html' };
  }
  return { recoverable: false, reason: 'not-download-like' };
}

export function filenameFromDownloadResponse(url: string, disposition: string): string {
  const match = /filename\*?=(?:UTF-8''|")?([^;"]+)/i.exec(disposition);
  if (match?.[1]) {
    try {
      return decodeURIComponent(match[1].trim());
    } catch {
      return match[1].trim();
    }
  }
  try {
    return decodeURIComponent(new URL(url).pathname.split('/').filter(Boolean).pop() || 'download');
  } catch {
    return 'download';
  }
}

export function safeDownloadUrl(rawUrl: string): string {
  try {
    const url = new URL(rawUrl);
    return `${url.origin}${url.pathname}`;
  } catch {
    return '<invalid-url>';
  }
}

export type DownloadNavigationStopResult =
  | { status: 'stopped' }
  | { status: 'page-closed' }
  | { status: 'page-changed'; currentUrl: string }
  | { status: 'failed'; error: string };

export type DownloadNavigationRecoveryInput = {
  page: Page;
  sourceUrl: string;
  url: string;
  startedAtMs: number;
};

type DownloadNavigationPageState =
  | { status: 'stalled' }
  | { status: 'page-closed' }
  | { status: 'page-changed'; currentUrl: string };

function inspectDownloadNavigationPageState(
  page: Page,
  sourceUrl: string
): DownloadNavigationPageState {
  if (page.isClosed()) return { status: 'page-closed' };
  const currentUrl = page.url();
  if (currentUrl !== sourceUrl) return { status: 'page-changed', currentUrl };
  return { status: 'stalled' };
}

export class DownloadNavigationRecoveryCoordinator {
  private readonly candidates = new Set<string>();

  constructor(
    private readonly input: {
      downloads: DownloadCaptureStore;
      getContext: () => Promise<BrowserContext>;
      logger: BrowserLogger;
    }
  ) {}

  /**
   * Chromium occasionally treats a cross-origin attachment as a main-frame
   * navigation and never emits `download`. Recover only URLs the page itself
   * requested, through the same authenticated browser context; model-supplied
   * URLs are never fetched here.
   */
  async recover(input: DownloadNavigationRecoveryInput): Promise<void> {
    const { page, sourceUrl, url, startedAtMs } = input;
    if (this.candidates.has(url)) return;
    this.candidates.add(url);
    try {
      // Give Playwright's native download event a chance first.
      await new Promise((resolve) => setTimeout(resolve, 750));
      if (hasObservedDownloadForUrl(this.input.downloads.downloads, url, startedAtMs)) {
        return;
      }
      const pageState = inspectDownloadNavigationPageState(page, sourceUrl);
      if (pageState.status !== 'stalled') {
        this.input.logger.debug('[DirectPlaywright] Download navigation recovery skipped', {
          url: safeDownloadUrl(url),
          reason: pageState.status,
          ...(pageState.status === 'page-changed' ? { currentUrl: pageState.currentUrl } : {}),
        });
        return;
      }
      const context = await this.input.getContext();
      const response = await context.request.get(url, { timeout: 15_000, failOnStatusCode: false });
      if (!response.ok()) {
        this.input.logger.debug('[DirectPlaywright] Download navigation response rejected', {
          url: safeDownloadUrl(url),
          reason: 'http-status',
          status: response.status(),
        });
        return;
      }
      const headers = response.headers();
      const lengthValidation = validateRecoveredDownloadContentLength(headers['content-length']);
      if (!lengthValidation.valid) {
        this.input.logger.debug('[DirectPlaywright] Download navigation response rejected', {
          url: safeDownloadUrl(url),
          reason: lengthValidation.reason,
          contentLength: headers['content-length'],
        });
        return;
      }
      const { contentLength } = lengthValidation;
      const contentType = headers['content-type']?.toLowerCase() ?? '';
      const disposition = headers['content-disposition'] ?? '';
      const classification = classifyDownloadResponse({
        url,
        contentType,
        contentDisposition: disposition,
      });
      this.input.logger.debug('[DirectPlaywright] Download navigation response classified', {
        url: safeDownloadUrl(url),
        contentType,
        contentLength,
        recoverable: classification.recoverable,
        reason: classification.reason,
      });
      if (!classification.recoverable) return;
      const buffer = await response.body();
      if (buffer.length === 0 || buffer.length > MAX_RECOVERED_DOWNLOAD_BYTES) {
        this.input.logger.debug('[DirectPlaywright] Download navigation body rejected', {
          url: safeDownloadUrl(url),
          reason: buffer.length === 0 ? 'empty-body' : 'body-too-large',
          sizeBytes: buffer.length,
        });
        return;
      }
      const filename = filenameFromDownloadResponse(url, disposition);
      await this.input.downloads.captureBuffer({ suggestedFilename: filename, url, buffer });
      const stopResult = await stopStalledDownloadNavigation({
        page,
        sourceUrl,
        logger: this.input.logger,
      });
      const stopLog = {
        url: safeDownloadUrl(url),
        status: stopResult.status,
        durationMs: Date.now() - startedAtMs,
        ...(stopResult.status === 'failed' ? { error: stopResult.error } : {}),
      };
      if (stopResult.status === 'failed') {
        this.input.logger.warn('[DirectPlaywright] Download navigation stop failed', stopLog);
      } else {
        this.input.logger.info('[DirectPlaywright] Download navigation stop completed', stopLog);
      }
    } catch (err) {
      this.input.logger.debug('[DirectPlaywright] Download navigation recovery skipped', {
        url: safeDownloadUrl(url),
        error: err instanceof Error ? err.message : String(err),
      });
    } finally {
      this.candidates.delete(url);
    }
  }
}

/** Stop only the initiating tab's still-pending attachment navigation. */
export async function stopStalledDownloadNavigation(input: {
  page: Page;
  sourceUrl: string;
  logger: BrowserLogger;
}): Promise<DownloadNavigationStopResult> {
  if (input.page.isClosed()) return { status: 'page-closed' };
  const currentUrl = input.page.url();
  if (currentUrl !== input.sourceUrl) return { status: 'page-changed', currentUrl };

  let session: CDPSession | null = null;
  try {
    session = await withOperationTimeout(
      input.page.context().newCDPSession(input.page),
      TIMEOUTS.UNWEDGE_PAGE_CAP,
      '[DirectPlaywright] create download-navigation CDP session'
    );
    if (input.page.isClosed()) return { status: 'page-closed' };
    const urlBeforeStop = input.page.url();
    if (urlBeforeStop !== input.sourceUrl) {
      return { status: 'page-changed', currentUrl: urlBeforeStop };
    }
    await withOperationTimeout(
      session.send('Page.stopLoading'),
      TIMEOUTS.UNWEDGE_PAGE_CAP,
      '[DirectPlaywright] Page.stopLoading for download navigation'
    );
    return { status: 'stopped' };
  } catch (err) {
    return { status: 'failed', error: err instanceof Error ? err.message : String(err) };
  } finally {
    if (session) {
      await withOperationTimeout(
        session.detach(),
        TIMEOUTS.UNWEDGE_PAGE_CAP,
        '[DirectPlaywright] detach download-navigation CDP session'
      ).catch((err: unknown) => {
        input.logger.debug('[DirectPlaywright] Download navigation CDP detach failed', {
          error: err instanceof Error ? err.message : String(err),
        });
      });
    }
  }
}
